/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import Radio from 'backbone.radio';

import HeaderBarView from '@/views/HeaderBarView';
import DropdownCell from '@/backgrid/DropdownCell';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';

import './OrderPackageHeaderView.css';

const OrderPackageHeaderView = HeaderBarView.extend({
  className: 'order-package-header-view header-bar-view clearfix',

  initialize() {
    const self = this;
    const columns = [
      {
        name: 'id',
        label: 'Order Package ID'
      },
      {
        name: 'status',
        label: 'Status'
      },
      {
        name: 'assignedCRS',
        label: 'Assigned CRS'
      },
      {
        name: 'assignedEPC',
        label: 'Assigned EPC'
      },
      {
        name: 'formName',
        label: 'Order Type'
      },
      {
        renderable: !EXTERNAL,
        label: '',
        name: '',
        headerCell: DropdownCell.extend({
          name: 'Actions',
          buttonClass: 'btn-outline-primary action-select mt-3 ms-auto me-3 col-3',
          buttonParentClass: `d-flex flex-row flex-grow-1 align-self-center`,
          optionFunction(data) {
            const options = [];
            if (self.options.showViewEditBtn) {
              if (self.options.isEditing) {
                options.push({ id: 'viewEditOrderPackage', display: 'View Order Package' });
              } else {
                options.push({ id: 'viewEditOrderPackage', display: 'Edit Order Package' });
              }
            }
            self.options.showReassignCRSBtn && options.push({ id: 'reassignCRS', display: 'Reassign CRS' });
            self.options.showReassignEPCBtn && options.push({ id: 'reassignEPC', display: 'Reassign EPC' });
            self.options.showUnClaimButton && options.push({ id: 'unclaim', display: 'Unclaim' });
            self.options.showPrintBtn && options.push({ id: 'print', display: 'Print' });
            self.options.showDeleteButton && options.push({ id: 'deleteDraft', display: 'Delete Draft' });
            self.options.showHideButton && options.push({ id: 'hide', display: 'Hide' });
            return options;
          },
          onClick(e, model) {
            const action = e.target.id;

            switch (action) {
              case 'viewEditOrderPackage': {
                const isEditing = self.options.isEditing;
                self.toggleIsEditing(e);
                this.render();
                return isEditing ? self.onClickView(e, model) : self.onClickEdit(e, false);
              }
              case 'reassignCRS':
                self.onClickReassignCRS(e);
                break;
              case 'reassignEPC':
                self.onClickReassignEPC(e);
                break;
              case 'unclaim': {
                self.onClickUnclaim(e);
                break;
              }
              case 'deleteDraft': {
                self.onClickDeleteDraft(e);
                break;
              }
              case 'print':
                self.onClickShowPrinterView(e);
                break;
              case 'hide':
                self.onClickHide(e);
                break;
            }
          }
        })
      }
    ].filter((col) => col.renderable !== false);

    this.options.columns = new Backbone.Collection(columns);

    this.listenTo(Radio.channel('order-package'), 'update:header', (key, value) => {
      this.model.set(key, value);
    });
    this.listenTo(this.model, 'backgrid:edited');
  },

  toggleIsEditing() {
    this.options.isEditing = !this.options.isEditing;
  },

  onClickEdit(e) {
    e.preventDefault();
    this.triggerMethod('reload', true);
  },

  onClickView(e, model) {
    e.preventDefault();
    this.triggerMethod('reload', false);
  },

  async onClickReassignCRS(e, model) {
    e.preventDefault();
    this.triggerMethod('reassign:crs', model);
  },

  async onClickReassignEPC(e, model) {
    e.preventDefault();
    this.triggerMethod('reassign:epc', model);
  },

  onClickShowPrinterView(e) {
    e.preventDefault();
    this.triggerMethod('print', true);
  },

  async onClickDeleteDraft(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to delete this order package?`;

    showModal({
      title: 'Delete Order Package',
      view: new ConfirmationModalView({
        confirmText: 'Delete',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('delete:draft', model);
            return Promise.resolve();
          }
        })
      })
    });
  },

  async onClickUnclaim(e, model) {
    e.preventDefault();
    this.triggerMethod('unclaim', model);
  },

  setAssignedCRS(name) {
    this.model.set('assignedCRS', name);
  },

  setAssignedEPC(name) {
    this.model.set('assignedEPC', name);
  },

  async onClickHide(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to hide this order package?`;

    showModal({
      title: 'Hide Order Package',
      view: new ConfirmationModalView({
        confirmText: 'Hide',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('hide', model);
            return Promise.resolve();
          }
        })
      })
    });
  }
});

export default OrderPackageHeaderView;
